import { getDatabase, ref, set, push, onValue } from 'firebase/database';
import Common from '../components/js/Common';

class ConventionTicketModel {
    constructor(userId, userData) {
        this.userId = userId;
        this.userData = userData;
        this.ticketPrice = 500; // 500 USDT
    }

    /**
     * Verifica si el usuario tiene saldo suficiente para comprar el ticket
     * @returns {Object} { canPurchase: boolean, availableBalance: number, walletCom: number, walletDiv: number }
     */
    checkBalance() {
        const walletCom = this.userData.walletCom || 0;
        const walletDiv = this.userData.walletDiv || 0;
        const totalAvailable = walletCom + walletDiv;

        return {
            canPurchase: totalAvailable >= this.ticketPrice,
            availableBalance: totalAvailable,
            walletCom: walletCom,
            walletDiv: walletDiv,
            required: this.ticketPrice,
            deficit: totalAvailable < this.ticketPrice ? this.ticketPrice - totalAvailable : 0
        };
    }

    /**
     * Verifica si el usuario ya compró un ticket
     * @returns {Promise<boolean>}
     */
    async hasTicket() {
        return new Promise((resolve) => {
            const db = getDatabase();
            const ticketRef = ref(db, `convention_tickets/${this.userId}`);
            
            onValue(ticketRef, (snapshot) => {
                const data = snapshot.val();
                resolve(!!data);
            }, { onlyOnce: true });
        });
    }

    /**
     * Compra el ticket y deduce el monto de las wallets
     * @param {number} fromComision - Monto a deducir de wallet de comisiones
     * @param {number} fromDividend - Monto a deducir de wallet de dividendos
     * @returns {Promise}
     */
    async purchaseTicket(fromComision, fromDividend) {
        const db = getDatabase();
        const common = new Common();

        try {
            // Verificar que ya tiene ticket
            const alreadyHasTicket = await this.hasTicket();
            if (alreadyHasTicket) {
                throw new Error('User already has a ticket');
            }

            // Verificar que la suma sea correcta
            if (fromComision + fromDividend !== this.ticketPrice) {
                throw new Error('Payment amounts do not match ticket price');
            }

            // Actualizar wallets del usuario
            const updatedUser = {
                ...this.userData,
                walletCom: this.userData.walletCom - fromComision,
                walletDiv: this.userData.walletDiv - fromDividend
            };

            await common.editAnyUser(updatedUser);

            // Crear registro del ticket
            const ticketData = {
                userId: this.userId,
                userName: this.userData.userName,
                firstName: this.userData.firstName,
                lastName: this.userData.lastName,
                email: this.userData.email,
                phoneNumber: this.userData.phoneNumber,
                country: this.userData.country,
                countryCode: this.userData.countryCode,
                ticketPrice: this.ticketPrice,
                paidFromComision: fromComision,
                paidFromDividend: fromDividend,
                purchaseDate: new Date().toISOString(),
                ticketNumber: this.generateTicketNumber(),
                status: 'confirmed' // confirmed, cancelled
            };

            const ticketRef = ref(db, `convention_tickets/${this.userId}`);
            await set(ticketRef, ticketData);

            // Registrar en el historial general
            const historyRef = ref(db, 'convention_tickets_history');
            const newHistoryRef = push(historyRef);
            await set(newHistoryRef, ticketData);

            return {
                success: true,
                ticketData: ticketData
            };

        } catch (error) {
            console.error('Error purchasing ticket:', error);
            throw error;
        }
    }

    /**
     * Genera un número de ticket único
     * @returns {string}
     */
    generateTicketNumber() {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `LV2026-${timestamp}-${random}`;
    }

    /**
     * Obtiene todos los tickets comprados
     * @param {Function} callback - Función callback para recibir los datos
     */
    static getAllTickets(callback) {
        const db = getDatabase();
        const ticketsRef = ref(db, 'convention_tickets');
        
        return onValue(ticketsRef, (snapshot) => {
            const data = snapshot.val();
            const ticketsArray = data ? Object.values(data) : [];
            callback(ticketsArray);
        });
    }

    /**
     * Obtiene el ticket de un usuario específico
     * @param {string} userId - ID del usuario
     * @param {Function} callback - Función callback
     */
    static getUserTicket(userId, callback) {
        const db = getDatabase();
        const ticketRef = ref(db, `convention_tickets/${userId}`);
        
        return onValue(ticketRef, (snapshot) => {
            const data = snapshot.val();
            callback(data);
        });
    }

    /**
     * Cancela un ticket (solo admin)
     * @param {string} userId - ID del usuario
     * @returns {Promise}
     */
    static async cancelTicket(userId) {
        const db = getDatabase();
        const ticketRef = ref(db, `convention_tickets/${userId}`);
        
        return set(ticketRef, null);
    }

    /**
     * Obtiene estadísticas de tickets
     * @returns {Promise}
     */
    static async getStatistics() {
        return new Promise((resolve) => {
            const db = getDatabase();
            const ticketsRef = ref(db, 'convention_tickets');
            
            onValue(ticketsRef, (snapshot) => {
                const data = snapshot.val();
                const tickets = data ? Object.values(data) : [];
                
                const stats = {
                    totalTickets: tickets.length,
                    totalRevenue: tickets.length * 500,
                    byCountry: {},
                    recentPurchases: tickets
                        .sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate))
                        .slice(0, 10)
                };

                // Agrupar por país
                tickets.forEach(ticket => {
                    const country = ticket.country || 'Unknown';
                    stats.byCountry[country] = (stats.byCountry[country] || 0) + 1;
                });

                resolve(stats);
            }, { onlyOnce: true });
        });
    }
}

export default ConventionTicketModel;
